import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { listPublishedArticles } from '../services/blog';
import './BlogPage.css';

const ARTICLES_PER_PAGE = 9;

function formatDate(d) {
  if (!d) return '';
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' }).format(d);
}

function BlogCard({ article }) {
  return (
    <Link to={`/blog/${article.slug}`} className="blog-card">
      <div className="blog-card-cover-wrap">
        {article.coverImage && (
          <img
            src={article.coverImage}
            alt=""
            className="blog-card-cover"
            loading="lazy"
          />
        )}
      </div>
      <div className="blog-card-body">
        {article.category && (
          <span className="blog-card-category">{article.category}</span>
        )}
        <h3 className="blog-card-title">{article.title}</h3>
        <p className="blog-card-excerpt">{article.excerpt}</p>
        <div className="blog-card-meta">
          {article.author} · {formatDate(article.publishedAt)}
        </div>
      </div>
    </Link>
  );
}

function BlogListPage() {
  const [articles, setArticles] = useState([]);
  const [state, setState] = useState('loading');
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('Tout');
  const [visibleCount, setVisibleCount] = useState(ARTICLES_PER_PAGE);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    listPublishedArticles({ max: 100 })
      .then((rows) => {
        if (cancelled) return;
        setArticles(rows);
        setState('ready');
      })
      .catch((err) => {
        console.error(err);
        if (cancelled) return;
        setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const cats = new Set(articles.map((a) => a.category).filter(Boolean));
    return ['Tout', ...Array.from(cats)];
  }, [articles]);

  const filtered = useMemo(() => {
    let result = articles;
    if (activeCategory !== 'Tout') {
      result = result.filter((a) => a.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (a) =>
          a.title?.toLowerCase().includes(q) ||
          a.excerpt?.toLowerCase().includes(q) ||
          a.category?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [articles, activeCategory, search]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  useEffect(() => {
    setVisibleCount(ARTICLES_PER_PAGE);
  }, [activeCategory, search]);

  const [featured, ...rest] = filtered;

  return (
    <div className="blog-wrapper">
      <title>Actu CrousRadar — astuces, guides et vie étudiante</title>
      <meta
        name="description"
        content="L'actu CrousRadar : astuces pour éviter la queue au RU, guides boursiers, vie étudiante et données sur les restaurants universitaires."
      />
      <link rel="canonical" href="https://crousradar.fr/blog" />
      <meta property="og:title" content="Actu CrousRadar" />
      <meta
        property="og:description"
        content="Astuces, guides et données sur la vie étudiante au CROUS."
      />
      <meta property="og:url" content="https://crousradar.fr/blog" />
      <meta property="og:type" content="website" />

      <div className="blog-container">
        <header className="blog-header">
          <div className="blog-header-top">
            <h1 className="blog-title">L'Actu</h1>
            <p className="blog-subtitle">Astuces, guides et décryptages pour la vie étudiante.</p>
          </div>

          <div className="blog-toolbar">
            {categories.length > 2 && (
              <div className="blog-filters">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    className={`blog-filter-pill ${activeCategory === cat ? 'active' : ''}`}
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
            <div className="blog-search">
              <Search size={18} className="blog-search-icon" />
              <input
                type="text"
                placeholder="Rechercher un article..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="blog-search-input"
              />
            </div>
          </div>
        </header>

        {state === 'loading' && <div className="blog-empty">Chargement…</div>}
        {state === 'error' && (
          <div className="blog-empty">
            Impossible de charger les articles pour le moment.
          </div>
        )}
        {state === 'ready' && filtered.length === 0 && (
          <div className="blog-empty">
            {search ? 'Aucun résultat pour cette recherche.' : 'Aucun article publié pour le moment.'}
          </div>
        )}

        {state === 'ready' && featured && !search && activeCategory === 'Tout' && (
          <Link to={`/blog/${featured.slug}`} className="blog-featured">
            <div className="blog-featured-cover-wrap">
              {featured.coverImage && (
                <img
                  src={featured.coverImage}
                  alt=""
                  className="blog-featured-cover"
                />
              )}
            </div>
            <div className="blog-featured-body">
              <span className="blog-featured-pill">
                À la une{featured.category ? ` · ${featured.category}` : ''}
              </span>
              <h2 className="blog-featured-title">{featured.title}</h2>
              <p className="blog-featured-excerpt">{featured.excerpt}</p>
              <div className="blog-featured-meta">
                {featured.author} · {formatDate(featured.publishedAt)}
              </div>
            </div>
          </Link>
        )}

        {state === 'ready' && visible.length > 0 && (
          <>
            <div className="blog-section-head">
              <h2>{activeCategory === 'Tout' && !search ? 'Tous les articles' : 'Résultats'}</h2>
              <small>{filtered.length} article{filtered.length > 1 ? 's' : ''}</small>
            </div>
            <div className="blog-grid">
              {(activeCategory === 'Tout' && !search ? rest.slice(0, visibleCount - 1) : visible).map((a) => (
                <BlogCard key={a.id} article={a} />
              ))}
            </div>

            {hasMore && (
              <div className="blog-load-more-wrap">
                <button
                  className="blog-load-more"
                  onClick={() => setVisibleCount((c) => c + ARTICLES_PER_PAGE)}
                >
                  Voir plus d'articles
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default BlogListPage;
